import { observer } from "mobx-react";
import { TFunction, TMessage } from "@/i18n/tt";
import { FC, ReactElement, useEffect, useMemo } from "react";
import { isOk, ok, Result } from "@/common/common";
import {
  createActionsProps,
  createEditComment,
  MatterForReview,
  MeetingEmailController,
  MeetingEmailInitializerController,
} from "@/noticeForBoardMember/noticeForBoardMemberDomain";
import {
  MatterView,
  ReviewMinutesBody,
} from "@/noticeForBoardMember/noticeForBoardmemberComponents";
import { noop } from "lodash";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { History } from "history";
import { runInAction } from "mobx";
import {
  useDispatch,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";

const ReviewMinutesAdminInitializer: FC<{
  documentId: string;
  orgNo: string;
  controller?: MeetingEmailInitializerController;
}> = observer(({ documentId, controller, orgNo }) => {
  const { t } = useTranslation();
  const { http } = useRootStore();
  const dispatch = useDispatch();
  const messageStore = useMessageStore();
  const history = useRootStore().history;

  const ctrl = useMemo(
    () => controller ?? new MeetingEmailInitializerController(dispatch, http, noop),
    [dispatch, http, controller],
  );

  useEffect(() => {
    ctrl.fetchPreviewForAdmin(documentId, orgNo);
  }, [ctrl, documentId, orgNo]);

  if (isOk(ctrl.result) && isOk(ctrl.result.data.matterResult)) {
    const meetingEmailController = ctrl.result.data;
    const actions = createActionsProps(
      meetingEmailController.response,
      meetingEmailController,
      history,
    );
    return (
      <MatterView
        matter={ctrl.result.data.matterResult.data}
        {...actions}
        onBack={() => isOk(ctrl.result) && (ctrl.result.data.matterResult = undefined)}
        editComment={createEditComment(meetingEmailController)}
        commentActions={{
          onSave: meetingEmailController.onSaveAsAdmin,
          onEditComment: meetingEmailController.setUpdateComment,
          onDeleteComment: meetingEmailController.setDeleteCommentId,
          onConfirmDelete: meetingEmailController.deleteCommentAsAdmin,
          onToggleEditor: meetingEmailController.onToggleEditor,
          onCancel: meetingEmailController.onToggleEditor,
          onChange: meetingEmailController.updateEditorText,
        }}
        messageStore={messageStore}
        fullscreen={false}
      />
    );
  } else {
    return render(t, ctrl.result, history, (matter: MatterForReview) => {
      runInAction(() => {
        if (isOk(ctrl.result)) {
          ctrl.result.data.matterResult = ok(matter);
        }
      });
    });
  }
});

function render(
  t: TFunction,
  result: Result<MeetingEmailController, TMessage>,
  history: History,
  onOpenMatter: (matter: MatterForReview) => void,
): ReactElement {
  switch (result.state) {
    case "pending":
      return <LoadingIndicator />;
    case "failure":
      return <Alert type={"error"} message={t(result.error)} />;
    case "cancelled":
      return <div />;
    case "ok": {
      const ctrl = result.data;
      const data = ctrl.response;
      const props = createActionsProps(data, ctrl, history);

      return (
        <ReviewMinutesBody
          {...props}
          isMultiLanguage={data.isMultiLanguage}
          mattersForReview={data.mattersForReview}
          uploadedFiles={data.uploadedFiles}
          comments={data.comments}
          onOpenMatter={onOpenMatter}
          onDownloadAttachment={ctrl.openPreviewAttachmentDialog}
          onCloseAttachmentPreview={ctrl.closePreviewAttachmentDialog}
          previewAttachmentState={ctrl.previewState}
          editComment={{ ...ctrl }}
          commentActions={{
            onSave: ctrl.onSaveAsAdmin,
            onEditComment: ctrl.setUpdateComment,
            onDeleteComment: ctrl.setDeleteCommentId,
            onConfirmDelete: ctrl.deleteCommentAsAdmin,
            onToggleEditor: ctrl.onToggleEditor,
            onCancel: ctrl.onToggleEditor,
            onChange: ctrl.updateEditorText,
          }}
        />
      );
    }
  }
}

export default ReviewMinutesAdminInitializer;
